import React, { useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Scale, FileText, Wrench, ShieldAlert, Award } from 'lucide-react';

export function TermsOfServicePage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={`pt-28 pb-20 transition-colors duration-500 ${isDark ? 'bg-dark-bg text-gray-300' : 'bg-bg-light text-gray-700'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/15 border border-accent/20 mb-4">
            <Scale className="w-4 h-4 text-accent" />
            <span className="text-xs uppercase font-poppins tracking-wider font-semibold text-accent animate-pulse">LEGAL PROTECTION</span>
          </div>
          <h1 className={`text-3xl sm:text-4xl lg:text-[44px] font-poppins font-bold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-primary'}`}>
            Terms of Service
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Last Updated: May 2026
          </p>
        </div>

        {/* Content Box */}
        <div className={`rounded-3xl p-6 sm:p-10 border transition-all duration-300 ${
          isDark 
            ? 'bg-[#111827]/80 border-white/[0.06] shadow-xl' 
            : 'bg-white border-black/[0.04] shadow-xl shadow-slate-100/50'
        }`}>
          
          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 flex items-center gap-2.5 ${isDark ? 'text-white' : 'text-primary'}`}>
              <FileText className="w-5 h-5 text-accent" />
              1. Agreement of Terms
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              Welcome to the website of <strong>Aura Spaces</strong> ("Aura Spaces by Harmony Home Designs"). By accessing our website, browsing our project portfolio, requesting modular layout cost estimations, or booking free design consultations, you agree to comply with and be bound by the following Terms of Service.
            </p>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 flex items-center gap-2.5 ${isDark ? 'text-white' : 'text-primary'}`}>
              <Wrench className="w-5 h-5 text-accent" />
              2. Scope of Design & Execution Services
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              Aura Spaces provides bespoke residential interior styling, modular kitchen manufacturing, customized wardrobes fabrication, and full turnkey civil-to-interior construction works.
            </p>
            <p className="text-sm leading-relaxed">
              <strong>tentative Estimates:</strong> All estimations provided by our online calculator, AI chatbot, or on-site layout drafts are strictly tentative and subject to change. Final exact prices are governed solely by official physical contracts signed between the client and Aura Spaces management prior to factory production.
            </p>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 flex items-center gap-2.5 ${isDark ? 'text-white' : 'text-primary'}`}>
              <ShieldAlert className="w-5 h-5 text-accent" />
              3. Client Cooperation & Approvals
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              To ensure on-time delivery of modular furniture and flawless carpentry execution, clients are requested to:
            </p>
            <ul className="space-y-3 pl-1">
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                <span>Provide continuous, safe access to the physical property site during approved on-site carpentry assembly hours.</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                <span>Review and sign off on 2D dimensional drawings and 3D color visual renders in a timely manner (carpentry cuts cannot be initiated without written sign-off).</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm leading-snug">
                <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                <span>Provide continuous water and electrical supply at the site required for machine cutting, profile channel alignments, and vacuum sanding.</span>
              </li>
            </ul>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 flex items-center gap-2.5 ${isDark ? 'text-white' : 'text-primary'}`}>
              <Award className="w-5 h-5 text-accent" />
              4. Intellectual Property & Showcase Rights
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              All website content, custom 3D model renderings, vector technical drawings, and photographs of completed interior works remain the exclusive intellectual property of Aura Spaces.
            </p>
            <p className="text-sm leading-relaxed">
              <strong>Showcase Photography:</strong> Aura Spaces reserves the right to capture high-definition photographs and video recordings of our finished interior transformations on-site for marketing and portfolio purposes. We will coordinate dates in advance with the property owners, respecting the privacy of household belongings.
            </p>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section className="mb-8">
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 ${isDark ? 'text-white' : 'text-primary'}`}>
              5. Warranty and Maintenance Limit
            </h2>
            <p className="text-sm leading-relaxed">
              We stand behind our craftsmanship. Aura Spaces offers a **5-Year Warranty** on all factory-pressed BWR Marine Plywood woodwork against manufacturing defects and natural delamination. Hardware fittings (Hettich, Ebco, Blum) are governed directly by their respective manufacturers' brand policies. Cosmetic wear and tear, water exposure damage on MR grade boards, or civil structural shifts are excluded.
            </p>
          </section>

          <hr className={`my-8 ${isDark ? 'border-gray-800' : 'border-gray-150/80'}`} />

          <section>
            <h2 className={`text-xl sm:text-2xl font-poppins font-bold mb-4 ${isDark ? 'text-white' : 'text-primary'}`}>
              6. Governance Contact Desk
            </h2>
            <p className="text-sm leading-relaxed mb-4">
              If you wish to report violations, dispute project timelines, or seek clarifications regarding our contract policies, please reach out to our legal department:
            </p>
            <div className={`p-5 rounded-2xl border text-sm leading-relaxed ${isDark ? 'bg-slate-900/60 border-gray-850' : 'bg-slate-50 border-slate-200/50'}`}>
              <strong className="block mb-1">Aura Spaces Contracts Governance</strong>
              Email: <a href="mailto:operations@auraspaces.net" className="text-accent font-bold hover:underline">operations@auraspaces.net</a><br />
              Phone: <span className="font-bold">+91 9989697942</span><br />
              HQ Studio: Gachibowli, Hyderabad, TS, India.
            </div>
          </section>

        </div>

      </div>
    </div>
  );
}
